import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/store/types";
import { useAppInitialization } from "@/app/routes/hooks/useAppInitialization";
import { Yard360Layout } from "@/app/layouts/Yard360Layout";
import Loader from "@amzn/meridian/loader";
import { ROUTES } from "@/app/app-constants";
import { isAllowListedAuthInfo } from "@/utils/allowListUtils";
import { RouteLoadingFallback } from "@/app/routes/components/RouteLoadingFallback";
import { RouteErrorBoundary } from "@/app/routes/components/RouteErrorBoundary";
import { ChildAppErrorBoundary } from "@/app/routes/components/ChildAppErrorBoundary";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import { ownerConfigHostServices } from "@/federation/ownerConfigHostServices";
import { hostlerPoolHostServices } from "@/federation/hostlerPoolHostServices";
import { relayYardTrackerHostServices } from "@/federation/relayYardTrackerHostServices";
import { agentStudioHostServices } from "@/federation/agentStudioHostServices";
import { eventHistoryHostServices } from "@/federation/eventHistoryHostServices";

// Lazy-load all route-level components for code splitting.
// The app shell (masthead + sidebar) stays visible during chunk download.
const AccuracyView = React.lazy(() => import("@/pages/relayYardControl/AccuracyView"));
const RelayYardTrackerApp = React.lazy(() => import("@amzn/ytyard360-relay-yard-tracker"));
const RyteTrackerView = React.lazy(() => import("@/pages/ryteTracker/RyteView"));
const OwnerConfigApp = React.lazy(() => import("@amzn/ytyard360-owner-config"));
const HostlerPoolApp = React.lazy(() => import("@amzn/ytyard360-hostler-pool"));
const AgentStudioApp = React.lazy(() => import("@amzn/ytyard360-agent-studio"));
const EventHistoryApp = React.lazy(() => import("@amzn/ytyard360-event-history"));
const WranglingView = React.lazy(() => import("@/pages/yard360/WranglingView"));
const EquipmentView = React.lazy(() => import("@/pages/yard360/EquipmentView"));
const HostSeamPlaceholder = React.lazy(
  () => import("@/federation/placeholder/HostSeamPlaceholder")
);

const selectRYCEnabled = (state: RootState) => state.featureFlags.enableRYC?.enabled;
const selectRYTEnabled = (state: RootState) => state.featureFlags.enableRYT?.enabled;
const selectRYTEEnabled = (state: RootState) => state.featureFlags.enableRYTE?.enabled;
const selectOwnerConfigurationEnabled = (state: RootState) =>
  state.featureFlags.enableOwnerConfiguration?.enabled;
const selectHostlerPoolEnabled = (state: RootState) =>
  state.featureFlags.enableHostlerPool?.enabled;
const selectAgentStudioEnabled = (state: RootState) =>
  state.featureFlags.enableAgentStudio?.enabled;
const selectHostSeamEnabled = (state: RootState) => state.featureFlags.enableHostSeam?.enabled;
const selectAuthInfo = (state: RootState) => state.authInfo;

export const RootRouter: React.FC = () => {
  const location = useLocation();
  const { error, isLoading } = useAppInitialization();
  const isRYCEnabledByFlag = useSelector(selectRYCEnabled);
  const isRYTEnabledByFlag = useSelector(selectRYTEnabled);
  const isRYTEEnabledByFlag = useSelector(selectRYTEEnabled);
  const isOwnerConfigurationEnabledByFlag = useSelector(selectOwnerConfigurationEnabled);
  const isHostlerPoolEnabledByFlag = useSelector(selectHostlerPoolEnabled);
  const isAgentStudioEnabledByFlag = useSelector(selectAgentStudioEnabled);
  const isHostSeamEnabledByFlag = useSelector(selectHostSeamEnabled);
  const authInfo = useSelector(selectAuthInfo);

  const isAllowListedUser = isAllowListedAuthInfo(authInfo);

  const isRYCEnabled = isAllowListedUser || isRYCEnabledByFlag;
  const isRYTEnabled = isAllowListedUser || isRYTEnabledByFlag;
  const isRYTEEnabled = isAllowListedUser || isRYTEEnabledByFlag;
  const isOwnerConfigurationEnabled = isAllowListedUser || isOwnerConfigurationEnabledByFlag;
  const isHostlerPoolEnabled = isAllowListedUser || isHostlerPoolEnabledByFlag;
  const isAgentStudioEnabled = isAllowListedUser || isAgentStudioEnabledByFlag;
  const isHostSeamEnabled = isAllowListedUser || isHostSeamEnabledByFlag;

  const areFeatureFlagsLoading =
    isRYCEnabledByFlag === undefined ||
    isRYTEnabledByFlag === undefined ||
    isOwnerConfigurationEnabledByFlag === undefined ||
    isHostlerPoolEnabledByFlag === undefined;
  const isEventReportRoute = location.pathname.startsWith(ROUTES.EVENT_REPORT);

  if (error) return <div>Error: {error}</div>;

  if (isLoading || (areFeatureFlagsLoading && !isEventReportRoute))
    return (
      <Column height="100%" alignmentHorizontal="center" alignmentVertical="center" heights="fit">
        <Row width="100%" alignmentHorizontal="center" alignmentVertical="center" widths="fit">
          <Loader size="large" />
        </Row>
      </Column>
    );

  return (
    <RouteErrorBoundary>
      <React.Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {/* RYC route */}
          <Route
            path={ROUTES.RYC}
            element={
              isRYCEnabled ? <AccuracyView /> : <Navigate replace to={ROUTES.APPOINTMENTS} />
            }
          />

          {/* RYT route (extracted package) */}
          <Route
            path={ROUTES.RYT}
            element={
              isRYTEnabled ? (
                <ChildAppErrorBoundary appName="RelayYardTracker">
                  <RelayYardTrackerApp hostServices={relayYardTrackerHostServices} />
                </ChildAppErrorBoundary>
              ) : (
                <Navigate replace to={ROUTES.APPOINTMENTS} />
              )
            }
          />

          {/* RYTE Tracker route */}
          <Route
            path={ROUTES.RYTE_TRACKER}
            element={
              isRYTEEnabled ? <RyteTrackerView /> : <Navigate replace to={ROUTES.APPOINTMENTS} />
            }
          />

          {/* Owner Configuration — mounted from @amzn/ytyard360-owner-config */}
          <Route
            path={`${ROUTES.OWNER_CONFIGURATION}/*`}
            element={
              isOwnerConfigurationEnabled ? (
                <ChildAppErrorBoundary appName="Owner Configuration">
                  <OwnerConfigApp hostServices={ownerConfigHostServices} />
                </ChildAppErrorBoundary>
              ) : (
                <Navigate replace to={ROUTES.APPOINTMENTS} />
              )
            }
          />

          {/* Hostler Pool route (extracted package) */}
          <Route
            path={ROUTES.HOSTLER_POOL}
            element={
              isHostlerPoolEnabled ? (
                <ChildAppErrorBoundary appName="HostlerPool">
                  <HostlerPoolApp hostServices={hostlerPoolHostServices} />
                </ChildAppErrorBoundary>
              ) : (
                <Navigate replace to={ROUTES.APPOINTMENTS} />
              )
            }
          />

          {/* Agent Studio route (extracted package) */}
          <Route
            path={`${ROUTES.AGENT_STUDIO}/*`}
            element={
              isAgentStudioEnabled ? (
                <ChildAppErrorBoundary appName="AgentStudio">
                  <AgentStudioApp hostServices={agentStudioHostServices} />
                </ChildAppErrorBoundary>
              ) : (
                <Navigate replace to={ROUTES.APPOINTMENTS} />
              )
            }
          />

          {/* Host Seam route */}
          <Route
            path={ROUTES.HOST_SEAM}
            element={
              isHostSeamEnabled ? (
                <HostSeamPlaceholder config={{ source: "host" }} />
              ) : (
                <Navigate replace to={ROUTES.APPOINTMENTS} />
              )
            }
          />

          {/* Yard360 routes */}
          <Route element={<Yard360Layout />}>
            <Route path={ROUTES.APPOINTMENTS} element={<WranglingView />} />
            <Route path={ROUTES.EQUIPMENT} element={<EquipmentView />} />
          </Route>

          <Route
            path={`${ROUTES.EVENT_REPORT}/*`}
            element={
              <ChildAppErrorBoundary appName="Event History">
                <EventHistoryApp hostServices={eventHistoryHostServices} />
              </ChildAppErrorBoundary>
            }
          />

          {/* Root level redirect */}
          <Route path="/" element={<Navigate replace to={ROUTES.RYC} />} />

          {/* Global catch-all route */}
          <Route path="*" element={<Navigate replace to={ROUTES.RYC} />} />
        </Routes>
      </React.Suspense>
    </RouteErrorBoundary>
  );
};
